import React, { useState, useEffect } from 'react'
import useModalFeedback from '../hooks/useModalFeedback'
import PhotoAttachmentPicker from '../components/PhotoAttachmentPicker'
import { getBaseUrl } from '../utils/apiConfig'

const API_BASE_URL = getBaseUrl()

export default function AttendanceForm() {
  const { showMessage, modalElement } = useModalFeedback()
  const hourOptions = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']

  const [employees, setEmployees] = useState([])
  const [attendances, setAttendances] = useState([
    {
      id: 'temp-1',
      employee_id: '',
      clock_in_hour: '',
      clock_in_ampm: 'AM',
      clock_out_hour: '',
      clock_out_ampm: 'PM',
      type: 'WD',
      isNew: true
    }
  ])
  const [attachments, setAttachments] = useState([])
  const [remarks, setRemarks] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(true)
  const [formOpenedAt] = useState(() => Date.now())

  useEffect(() => {
    fetchEmployees()
  }, [])

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/employees/public-list`)
      const payload = await response.json()
      const allEmployees = payload?.data || []
      setEmployees(allEmployees)
      setLoading(false)
    } catch (error) {
      console.error('Failed to fetch employees:', error)
      setLoading(false)
    }
  }

  const handleAttendanceChange = (id, field, value) => {
    setAttendances(attendances.map(att =>
      att.id === id ? { ...att, [field]: value } : att
    ))
  }

  const to24HourTime = (hour12, ampm) => {
    const hourNum = parseInt(hour12, 10)
    if (!hourNum || hourNum < 1 || hourNum > 12) return ''

    let hour24 = hourNum
    if (ampm === 'AM') {
      hour24 = hourNum === 12 ? 0 : hourNum
    } else {
      hour24 = hourNum === 12 ? 12 : hourNum + 12
    }

    return `${String(hour24).padStart(2, '0')}:00:00`
  }

  // Working-hour rules (no fixed clock window — any shift length counts).
  // Mirrors the backend algorithm exactly.
  // 1. A shift spanning >= 9 clocked hours includes a 1-hour rest break,
  //    so 9h on the clock = 8 net working hours.
  // 2. Net working hours above 8 per day are OVERTIME.
  // 3. Net working hours below 8 are HALF-DAY hours (normalHours < 8).
  const REST_DEDUCTION_THRESHOLD = 9 * 60 // 540 min (9h) on the clock
  const REST_MINUTES = 60                 // 1h rest break
  const FULL_DAY_MINUTES = 8 * 60         // 480 min (8h) normal per day

  // Split a clock-in/clock-out interval into normal and overtime hours.
  // Supports overnight shifts that wrap past midnight.
  const splitHoursByRange = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return { totalHours: 0, normalHours: 0, otHours: 0 }

    const [inH, inM] = clockIn.split(':').map(Number)
    const [outH, outM] = clockOut.split(':').map(Number)

    let inMinutes = inH * 60 + (inM || 0)
    let outMinutes = outH * 60 + (outM || 0)

    if (outMinutes <= inMinutes) outMinutes += 24 * 60 // overnight wrap

    const grossMinutes = outMinutes - inMinutes

    // Rule 1: shifts of 9+ clocked hours lose a 1-hour rest break.
    const netMinutes = grossMinutes >= REST_DEDUCTION_THRESHOLD
      ? grossMinutes - REST_MINUTES
      : grossMinutes

    // Rule 2: net hours above 8 are OT. Rule 3: below 8 stays as half-day normal hours.
    const normalMinutes = Math.min(netMinutes, FULL_DAY_MINUTES)
    const otMinutes = Math.max(netMinutes - FULL_DAY_MINUTES, 0)

    const normalHours = Number(Math.max(normalMinutes / 60, 0).toFixed(2))
    const otHours = Number((otMinutes / 60).toFixed(2))
    const totalHours = Number((normalHours + otHours).toFixed(2))

    return { totalHours, normalHours, otHours }
  }

  const getCalculatedAmount = (attendanceRow, normalHours, otHours) => {
    const employee = employees.find(emp => String(emp.employee_id) === String(attendanceRow.employee_id))
    const dailySalary = Number(employee?.daily_salary || 0)
    const monthlySalary = Number(employee?.monthly_salary || 0)
    const weekdayRate = Number(employee?.hour_rate || 0)
    const weekendRate = Number(employee?.weekend_rate || 0)
    const publicHolidayRate = Number(employee?.public_holiday_rate || 0)
    const isWorker = employee?.position === 'Worker'
    if (!isWorker && attendanceRow?.type === 'WE') {
      return Number(weekendRate.toFixed(2))
    }
    if (!isWorker && attendanceRow?.type === 'PH') {
      return Number(publicHolidayRate.toFixed(2))
    }
    let effectiveRate = weekdayRate
    if (attendanceRow?.type === 'WE') effectiveRate = weekendRate
    if (attendanceRow?.type === 'PH') effectiveRate = publicHolidayRate
    const payableNormalHours = Number(normalHours || 0)
    const useMonthly = monthlySalary > 0

    if (!useMonthly) {
      const dayBase = (otHours * 1.5 * effectiveRate) + (payableNormalHours * effectiveRate)
      if (Number(normalHours || 0) < 8) {
        return Number(dayBase.toFixed(2)) // half-day / no salary
      }
      return Number((dailySalary + dayBase).toFixed(2))
    }

    return Number(((otHours * effectiveRate) + (payableNormalHours * effectiveRate)).toFixed(2))
  }

  const getAttendanceCalculation = (attendanceRow) => {
    const clockIn = to24HourTime(attendanceRow.clock_in_hour, attendanceRow.clock_in_ampm)
    const clockOut = to24HourTime(attendanceRow.clock_out_hour, attendanceRow.clock_out_ampm)
    const { totalHours, normalHours, otHours } = splitHoursByRange(clockIn, clockOut)
    const amount = getCalculatedAmount(attendanceRow, normalHours, otHours)

    return {
      clockIn,
      clockOut,
      totalHours,
      normalHours,
      otHours,
      amount
    }
  }

  const addRow = () => {
    const newAttendance = {
      id: `temp-${Date.now()}`,
      employee_id: '',
      clock_in_hour: '',
      clock_in_ampm: 'AM',
      clock_out_hour: '',
      clock_out_ampm: 'PM',
      type: 'WD',
      isNew: true
    }
    setAttendances([...attendances, newAttendance])
  }

  const removeRow = (id) => {
    if (attendances.length > 1) {
      setAttendances(attendances.filter(att => att.id !== id))
    }
  }

  const addAttachments = (newFiles) => {
    if (!newFiles || newFiles.length === 0) return

    const mapped = newFiles[0]?.file
      ? newFiles
      : Array.from(newFiles).map((file) => ({
          id: `file-${Date.now()}-${Math.random()}`,
          name: file.name,
          size: file.size,
          file
        }))

    setAttachments((prev) => [...prev, ...mapped])
  }

  const removeAttachment = (id) => {
    setAttachments(attachments.filter(att => att.id !== id))
  }

  const validateForm = () => {
    // Filter out empty rows (where employee_id is not selected)
    const filledRows = attendances.filter(att => att.employee_id)
    
    if (filledRows.length === 0) {
      showMessage('Please add at least one timesheet record', 'Validation')
      return false
    }
    
    // Validate only filled rows
    for (let att of filledRows) {
      if (!att.clock_in_hour || !att.clock_out_hour) {
        showMessage('Please enter clock in and clock out for all selected employees', 'Validation')
        return false
      }
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      setSubmitting(true)

      // Submit only filled attendance records (skip empty rows)
      const filledAttendances = attendances.filter(att => att.employee_id)

      // Step 1: Upload photos once if there are any attachments
      let photoPaths = []
      if (attachments.length > 0) {
        const photoFormData = new FormData()
        attachments.forEach(attachment => {
          photoFormData.append('attachments', attachment.file)
        })

        const photoResponse = await fetch(`${API_BASE_URL}/api/attendance/public-upload-photos`, {
          method: 'POST',
          body: photoFormData
        })

        const photoData = await photoResponse.json()

        if (!photoResponse.ok) {
          throw new Error(photoData.message || 'Failed to upload photos')
        }

        photoPaths = photoData.data || []
      }

      // Step 2: Submit each worker with the photo paths (no duplicate uploads)
      for (let att of filledAttendances) {
        const calculation = getAttendanceCalculation(att)

        const formData = new FormData()
        formData.append('employee_id', att.employee_id)
        formData.append('clock_in', calculation.clockIn)
        formData.append('clock_out', calculation.clockOut)
        formData.append('type', att.type)
        formData.append('date_submission', new Date().toISOString().split('T')[0])
        formData.append('submitted_at', String(formOpenedAt))
        formData.append('website', '')
        if (remarks.trim()) {
          formData.append('remarks', remarks.trim())
        }

        // Send photo paths instead of files (optimized)
        if (photoPaths.length > 0) {
          formData.append('photo_paths', JSON.stringify(photoPaths))
        }

        const response = await fetch(`${API_BASE_URL}/api/attendance/public-submit`, {
          method: 'POST',
          body: formData
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.message || data.error || 'Failed to submit timesheet')
        }
      }

      setSubmitted(true)
      setTimeout(() => {
        setSubmitted(false)
        setAttendances([
          {
            id: 'temp-1',
            employee_id: '',
            clock_in_hour: '',
            clock_in_ampm: 'AM',
            clock_out_hour: '',
            clock_out_ampm: 'PM',
            type: 'WD',
            isNew: true
          }
        ])
        setAttachments([])
        setRemarks('')
      }, 3000)
    } catch (error) {
      console.error('Failed to submit attendance:', error)
      showMessage(error.message || 'Failed to submit timesheet', 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 max-w-4xl w-full text-center">
          <div className="text-5xl mb-3">✅</div>
          <h2 className="text-xl font-semibold text-green-700 mb-2">Success!</h2>
          <p className="text-green-600 mb-2">{attendances.filter(a => a.employee_id).length} timesheet record(s) submitted successfully.</p>
          <p className="text-sm text-gray-600">You can now submit another record or close this window.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-blue-50 to-white px-3 py-4 sm:px-4 sm:py-6 lg:py-8">
      <div className="mx-auto w-full max-w-5xl">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-5 sm:px-6 sm:py-6">
            <p className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
              Public Form
            </p>
            <h1 className="mt-3 text-2xl font-bold text-slate-900 sm:text-3xl">Timesheet Submission Form</h1>
            <p className="mt-2 text-sm text-slate-600">Submit one or more timesheet records with photo attachments.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 px-4 py-5 sm:px-6 sm:py-6">
            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="text-sm font-semibold text-slate-800">
                  Timesheet Records <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={addRow}
                  disabled={submitting}
                  className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  + Add Row
                </button>
              </div>

              <div className="space-y-3 lg:hidden">
                {attendances.map((att, index) => (
                  <article key={att.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500">Record {index + 1}</p>
                      {attendances.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRow(att.id)}
                          disabled={submitting}
                          className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs font-medium text-slate-600">Employee</label>
                        <select
                          value={att.employee_id}
                          onChange={(e) => handleAttendanceChange(att.id, 'employee_id', e.target.value)}
                          disabled={submitting}
                          className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">-- Select Employee --</option>
                          {employees.map((emp) => (
                            <option key={emp.employee_id} value={emp.employee_id}>
                              {emp.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-600">Clock In</label>
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={att.clock_in_hour}
                            onChange={(e) => handleAttendanceChange(att.id, 'clock_in_hour', e.target.value)}
                            disabled={submitting}
                            className="h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Hour</option>
                            {hourOptions.map((hour) => (
                              <option key={`in-${att.id}-${hour}`} value={hour}>{hour}</option>
                            ))}
                          </select>
                          <select
                            value={att.clock_in_ampm}
                            onChange={(e) => handleAttendanceChange(att.id, 'clock_in_ampm', e.target.value)}
                            disabled={submitting}
                            className="h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-600">Clock Out</label>
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={att.clock_out_hour}
                            onChange={(e) => handleAttendanceChange(att.id, 'clock_out_hour', e.target.value)}
                            disabled={submitting}
                            className="h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Hour</option>
                            {hourOptions.map((hour) => (
                              <option key={`out-${att.id}-${hour}`} value={hour}>{hour}</option>
                            ))}
                          </select>
                          <select
                            value={att.clock_out_ampm}
                            onChange={(e) => handleAttendanceChange(att.id, 'clock_out_ampm', e.target.value)}
                            disabled={submitting}
                            className="h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs font-medium text-slate-600">Type</label>
                        <select
                          value={att.type}
                          onChange={(e) => handleAttendanceChange(att.id, 'type', e.target.value)}
                          disabled={submitting}
                          className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="WD">Workdays</option>
                          <option value="WE">Weekend</option>
                          <option value="PH">Public Holiday</option>
                        </select>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              <div className="hidden overflow-x-auto overflow-y-auto max-h-[70vh] rounded-xl border border-slate-200 lg:block">
                <table className="w-full min-w-[820px] text-sm">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide">#</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide">Employee Name</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide">Clock In</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide">Clock Out</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide">Type</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendances.map((att, index) => (
                      <tr key={att.id} className="border-t border-slate-100 hover:bg-blue-50/50">
                        <td className="px-3 py-2.5 text-xs font-semibold text-slate-500">{index + 1}</td>
                        <td className="px-3 py-2.5">
                          <select
                            value={att.employee_id}
                            onChange={(e) => handleAttendanceChange(att.id, 'employee_id', e.target.value)}
                            disabled={submitting}
                            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">-- Select --</option>
                            {employees.map((emp) => (
                              <option key={emp.employee_id} value={emp.employee_id}>
                                {emp.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="grid grid-cols-2 gap-2">
                            <select
                              value={att.clock_in_hour}
                              onChange={(e) => handleAttendanceChange(att.id, 'clock_in_hour', e.target.value)}
                              disabled={submitting}
                              className="h-10 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Hour</option>
                              {hourOptions.map((hour) => (
                                <option key={`desk-in-${att.id}-${hour}`} value={hour}>{hour}</option>
                              ))}
                            </select>
                            <select
                              value={att.clock_in_ampm}
                              onChange={(e) => handleAttendanceChange(att.id, 'clock_in_ampm', e.target.value)}
                              disabled={submitting}
                              className="h-10 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="AM">AM</option>
                              <option value="PM">PM</option>
                            </select>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="grid grid-cols-2 gap-2">
                            <select
                              value={att.clock_out_hour}
                              onChange={(e) => handleAttendanceChange(att.id, 'clock_out_hour', e.target.value)}
                              disabled={submitting}
                              className="h-10 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Hour</option>
                              {hourOptions.map((hour) => (
                                <option key={`desk-out-${att.id}-${hour}`} value={hour}>{hour}</option>
                              ))}
                            </select>
                            <select
                              value={att.clock_out_ampm}
                              onChange={(e) => handleAttendanceChange(att.id, 'clock_out_ampm', e.target.value)}
                              disabled={submitting}
                              className="h-10 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="AM">AM</option>
                              <option value="PM">PM</option>
                            </select>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <select
                            value={att.type}
                            onChange={(e) => handleAttendanceChange(att.id, 'type', e.target.value)}
                            disabled={submitting}
                            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="WD">Workdays</option>
                            <option value="WE">Weekend</option>
                            <option value="PH">Public Holiday</option>
                          </select>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <button
                              type="button"
                              onClick={() => removeRow(att.id)}
                              disabled={submitting}
                              title="Delete"
                              className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
                            >
                              🗑️
                            </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div>
              <label className="block text-sm font-semibold text-slate-800 mb-1">
                Remarks
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                disabled={submitting}
                rows={3}
                placeholder="Optional notes or comments..."
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>

            <PhotoAttachmentPicker
              label="Upload Attachments"
              files={attachments}
              onAddFiles={addAttachments}
              onRemoveFile={removeAttachment}
              disabled={submitting}
              multiple
            />

            <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
              <p className="text-sm text-blue-800">Tip: You can add multiple rows before submitting, then attach photos in one go.</p>
            </div>

            <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setAttendances([
                    {
                      id: 'temp-1',
                      employee_id: '',
                      clock_in_hour: '',
                      clock_in_ampm: 'AM',
                      clock_out_hour: '',
                      clock_out_ampm: 'PM',
                      type: 'WD',
                      isNew: true
                    }
                  ])
                  setAttachments([])
                  setRemarks('')
                }}
                disabled={submitting}
                className="h-11 rounded-lg border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={submitting || attendances.filter(a => a.employee_id).length === 0}
                className="h-11 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400"
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
          {modalElement}
        </div>
      </div>
    </div>
  )
}
